import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { AudioWaveform, Library, ListOrdered, FlaskConical, Cpu, SlidersHorizontal, Users, LogOut, Coins, Clapperboard, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { to: "/episodes", label: "Episodes", icon: Clapperboard, id: "nav-episodes-tab" },
];
const ADVANCED = [
  { to: "/media", label: "Media Library", icon: Library, id: "nav-media-library-tab" },
  { to: "/jobs", label: "Jobs Queue", icon: ListOrdered, id: "nav-jobs-queue-tab" },
  { to: "/qc", label: "QC Console", icon: SlidersHorizontal, id: "nav-qc-console-tab" },
  { to: "/benchmark", label: "Benchmark Lab", icon: FlaskConical, id: "nav-benchmark-lab-tab" },
  { to: "/engines", label: "Engines", icon: Cpu, id: "nav-engine-settings-tab" },
  { to: "/costs", label: "Costs", icon: Coins, id: "nav-costs-tab" },
];

export const TopBar = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [sys, setSys] = useState(null);
  useEffect(() => {
    const load = () => {
      api.get("/jobs/stats").then((r) => setStats(r.data)).catch(() => {});
    };
    load();
    api.get("/system").then((r) => setSys(r.data)).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  const active = stats?.by_status?.processing || 0;
  const queued = stats?.by_status?.queued || 0;
  return (
    <header data-testid="main-navigation-bar" className="sticky top-0 z-40 border-b border-[#253044] bg-[#121721]/90 backdrop-blur-md">
      <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-14 flex items-center gap-6">
        <div className="flex items-center gap-2.5 min-w-fit">
          <div className="h-8 w-8 rounded-sm bg-[#E11D48] grid place-items-center">
            <AudioWaveform className="h-4.5 w-4.5 text-white" size={18} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[15px] tracking-tight">Gulmohar</div>
            <div className="micro -mt-0.5">M&E Separation Engine</div>
          </div>
        </div>
        <nav className="flex items-center gap-1 flex-1">
          {TABS.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} data-testid={id}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-[#1E2738] text-white" : "text-slate-400 hover:text-slate-100 hover:bg-[#1A2130]"}`}>
              <Icon size={15} /> <span className="hidden md:inline">{label}</span>
            </NavLink>
          ))}
          {user?.role !== "admin" && (
            <NavLink to="/qc" data-testid="nav-qc-console-tab" className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-[#1E2738] text-white" : "text-slate-400 hover:text-slate-100 hover:bg-[#1A2130]"}`}>
              <SlidersHorizontal size={15} /> <span className="hidden md:inline">QC Console</span>
            </NavLink>
          )}
          {user?.role === "admin" && <div className="relative group">
            <button data-testid="nav-advanced-menu" className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium text-slate-400 hover:text-slate-100 hover:bg-[#1A2130]"><Cpu size={15} /> <span className="hidden md:inline">Advanced</span> <ChevronDown size={13} /></button>
            <div className="absolute left-0 top-full pt-1 hidden group-hover:block group-focus-within:block z-50">
              <div className="min-w-[200px] rounded-md border border-[#253044] bg-[#121721] shadow-xl p-1">
                {ADVANCED.map(({ to, label, icon: Icon, id }) => (
                  <NavLink key={to} to={to} data-testid={id} className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-sm text-sm whitespace-nowrap ${isActive ? "bg-[#1E2738] text-white" : "text-slate-300 hover:bg-[#1A2130]"}`}><Icon size={14} /> {label}</NavLink>
                ))}
                <NavLink to="/team" data-testid="nav-team-tab" className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-sm text-sm ${isActive ? "bg-[#1E2738] text-white" : "text-slate-300 hover:bg-[#1A2130]"}`}><Users size={14} /> Team</NavLink>
              </div>
            </div>
          </div>}
          {false && (
            <NavLink to="/team" data-testid="nav-team-tab-legacy"
              className={({ isActive }) => `flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-[#1E2738] text-white" : "text-slate-400 hover:text-slate-100 hover:bg-[#1A2130]"}`}>
              <Users size={15} /> <span className="hidden md:inline">Team</span>
            </NavLink>
          )}
        </nav>
        <div className="flex items-center gap-4 text-xs font-mono text-slate-400 shrink-0 whitespace-nowrap">
          <span data-testid="header-queue-status" className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${active ? "bg-sky-400 pulse-dot" : "bg-slate-600"}`} />
            {active} processing · {queued} queued
          </span>
          <span data-testid="header-storage-status" className="hidden lg:flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${sys?.storage?.initialized ? "bg-emerald-400" : "bg-amber-400"}`} />
            {sys?.storage?.backend || "storage"}
          </span>
          <span data-testid="header-user-name" className="hidden md:inline text-slate-300">{user?.name} <span className="text-slate-600">· {user?.role}</span></span>
          <button data-testid="logout-button" onClick={logout} title="Sign out" className="h-7 w-7 grid place-items-center rounded-sm border border-[#253044] hover:bg-[#1E2738] text-slate-400 hover:text-white"><LogOut size={13} /></button>
        </div>
      </div>
    </header>
  );
};
